// Admin — Role permission matrix (read-only) — Wave E.
//
// Static reference of which permissions each role grants. This is a UI
// surface only; the server is the source of truth for actual enforcement.
// Update the table here when CLIENT_DECISIONS.md role gates change.

import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Minus, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import styles from './RolesPage.module.css';

interface Permission {
  group: string;
  label: string;
  staff: boolean;
  manager: boolean;
  admin: boolean;
}

const PERMISSIONS: Permission[] = [
  // Operations
  { group: 'Operations', label: 'View dashboard', staff: true, manager: false, admin: true },
  { group: 'Operations', label: 'View / edit diary', staff: true, manager: false, admin: true },
  { group: 'Operations', label: 'Create / modify bookings', staff: true, manager: false, admin: true },
  { group: 'Operations', label: 'Cancel bookings', staff: true, manager: false, admin: true },
  { group: 'Operations', label: 'Manage pending queue', staff: true, manager: false, admin: true },
  // Customers
  { group: 'Customers', label: 'View customer list', staff: true, manager: false, admin: true },
  { group: 'Customers', label: 'View customer banking', staff: false, manager: false, admin: true },
  { group: 'Customers', label: 'Merge / hard-delete customers', staff: false, manager: false, admin: true },
  // Receipts
  { group: 'Receipts', label: 'Create / send COVD receipts', staff: true, manager: false, admin: true },
  { group: 'Receipts', label: 'Edit / delete receipts', staff: false, manager: false, admin: true },
  { group: 'Receipts', label: 'Bulk export receipts', staff: false, manager: false, admin: true },
  // Finance
  { group: 'Finance', label: 'View invoices / payment run', staff: true, manager: false, admin: true },
  { group: 'Finance', label: 'Generate BACS / sync QuickBooks', staff: false, manager: false, admin: true },
  // Admin
  { group: 'Admin', label: 'Manage users (staff)', staff: false, manager: false, admin: true },
  { group: 'Admin', label: 'Manage drivers', staff: false, manager: false, admin: true },
  { group: 'Admin', label: 'Edit company settings', staff: false, manager: false, admin: true },
  { group: 'Admin', label: 'Manage message templates', staff: false, manager: false, admin: true },
  { group: 'Admin', label: 'View audit log', staff: false, manager: false, admin: true },
  { group: 'Admin', label: 'Data export', staff: false, manager: false, admin: true },
  // Skip module (manager only in v1)
  { group: 'Skip module', label: 'Access skip / bin operations', staff: false, manager: true, admin: false },
];

function Cell({ allowed }: { allowed: boolean }): JSX.Element {
  if (allowed) {
    return (
      <span className={styles.cellAllowed} aria-label="Allowed">
        <Check size={16} aria-hidden />
      </span>
    );
  }
  return (
    <span className={styles.cellDenied} aria-label="Not allowed">
      <Minus size={16} aria-hidden />
    </span>
  );
}

export function RolesPage(): JSX.Element {
  const navigate = useNavigate();

  // Group permissions by category
  const grouped = PERMISSIONS.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className={styles.page}>
      <PageHeader
        title="Role Permissions"
        subtitle="Read-only reference of what each role can do"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        }
      />

      <Card flush>
        <table className={styles.table} aria-label="Role permission matrix">
          <thead>
            <tr>
              <th scope="col" className={styles.permHeader}>
                <ShieldCheck size={14} aria-hidden /> Permission
              </th>
              <th scope="col">Staff</th>
              <th scope="col">Manager</th>
              <th scope="col">Admin</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([group, items]) => (
              <Fragment key={`g-${group}`}>
                <tr className={styles.groupRow}>
                  <th scope="rowgroup" colSpan={4} className={styles.groupHead}>
                    {group}
                  </th>
                </tr>
                {items.map((p) => (
                  <tr key={`${group}-${p.label}`}>
                    <th scope="row" className={styles.permLabel}>
                      {p.label}
                    </th>
                    <td>
                      <Cell allowed={p.staff} />
                    </td>
                    <td>
                      <Cell allowed={p.manager} />
                    </td>
                    <td>
                      <Cell allowed={p.admin} />
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
